import { useTranslation } from 'next-i18next';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { usePlotsByEnityId } from '@/modules/periodicJobs/apihooks';
import LocationDropdown from '@/modules/periodicJobs/components/LocationDropdown';
import { IWorkLocationProps } from '@/modules/periodicJobs/components/WorkLocation/IWorkLocation';
import { PeriodicJobsContext } from '@/modules/periodicJobs/context/context';
import { Typography } from '@/shared/components/atoms';

import Arrow from '~/images/shared/arrowDown.svg';

const WorkLocation: React.FC<IWorkLocationProps> = ({ entityId }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { state } = useContext(PeriodicJobsContext);

  const { t } = useTranslation('common');

  const { data, fetchNextPage, hasNextPage } = usePlotsByEnityId(
    entityId,
    state.selectedCrop?.id
  );

  const plotsArray = data?.pages.flatMap((page) =>
    page.plots.flatMap((plot) => plot)
  );

  const workLocationRef = useRef<HTMLDivElement>(null);

  const closeDropdownHandler = () => {
    setIsOpen(false);
  };

  const toggleDropdownHandler = () => {
    setIsOpen((prevState) => !prevState);
  };

  const closeOpenMenus = useCallback(
    (e: MouseEvent) => {
      if (
        isOpen &&
        workLocationRef.current &&
        !workLocationRef.current.contains(e.target as Node)
      ) {
        closeDropdownHandler();
      }
    },
    [workLocationRef, isOpen]
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeOpenMenus);

    return () => {
      document.removeEventListener('mousedown', closeOpenMenus);
    };
  }, [workLocationRef, closeOpenMenus]);

  return (
    <div
      ref={workLocationRef}
      className='relative select-none rounded-xl bg-primary-12 px-7'
    >
      <div
        className='flex cursor-pointer items-center gap-x-2'
        onClick={toggleDropdownHandler}
      >
        <Typography
          component='h6'
          weight='semiBold'
          className='py-16 text-primary-default'
        >
          {t('work-locations')}
        </Typography>
        <Arrow />
      </div>
      {isOpen && (
        <LocationDropdown
          onClose={closeDropdownHandler}
          onConfirm={closeDropdownHandler}
          plots={plotsArray}
          loadMore={() => fetchNextPage()}
          hasMore={hasNextPage}
        />
      )}
    </div>
  );
};

export default WorkLocation;
